import { FirebaseConfig } from '@/types/firebase';

/** Use app's domain as authDomain - required for signInWithRedirect on Safari/iOS (third-party storage blocking).
 *  When deployed to web.app/firebaseapp.com, ALWAYS use hostname (overrides env) so auth stays same-origin.
 *  Add https://<your-domain>/__/auth/handler to Google OAuth redirect URIs.
 *
 *  On localhost: Do NOT use window.location.host. The Firebase JS SDK hardcodes https:// when building
 *  auth handler URLs (firebase/firebase-js-sdk#7342), so authDomain=localhost:3000 yields
 *  https://localhost:3000/__/auth/handler which is unreachable (dev server uses HTTP). Use the
 *  Firebase project auth domain instead so redirects go to https://<project>.firebaseapp.com/__/auth/handler. */
function getAuthDomain(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) return host;
    // localhost: use Firebase auth domain (SDK hardcodes https, so localhost would be unreachable)
  }
  const env = process.env.REACT_APP_AUTH_DOMAIN?.trim();
  if (env) return env;
  const projectId = process.env.REACT_APP_PROJECT_ID?.trim();
  return projectId ? `${projectId}.firebaseapp.com` : '';
}

export const firebaseConfig: FirebaseConfig = {
  apiKey: process.env.REACT_APP_APIKEY?.trim() || '',
  authDomain: getAuthDomain(),
  projectId: process.env.REACT_APP_PROJECT_ID?.trim() || '',
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET?.trim() || '',
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID?.trim() || '',
  appId: process.env.REACT_APP_APP_ID?.trim() || '',
  measurementId: process.env.REACT_APP_MEASUREMENT_ID?.trim() || '',
};

export const COLLECTIONS = {
  USERS: 'Users',
  SUPERMARKETS: 'Supermarkets',
  PRODUCTS: 'Products',
  CATEGORIES: 'Categories',
  INVENTORY: 'Inventory',
  CARTS: 'Carts',
  CART_INSTANCES: 'CartInstances',
  TRANSACTIONS: 'Transactions',
  DELIVERIES: 'Deliveries',
  WIPAY_PAYMENTS: 'WipayPayments',
  SUPERMARKET_REVIEWS: 'SupermarketReviews',
} as const;

export const STORAGE_PATHS = {
  PRODUCT_IMAGES: 'product-images',
  SUPERMARKET_IMAGES: 'supermarket-images',
  USER_AVATARS: 'user-avatars',
  DOCUMENTS: 'documents',
} as const;

export const FUNCTIONS_REGION = 'us-central1' as const;

export const PAGINATION_DEFAULTS = {
  PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const;

export const CACHE_DURATION = {
  SHORT: 5 * 60 * 1000, // 5 minutes
  MEDIUM: 30 * 60 * 1000, // 30 minutes
  LONG: 24 * 60 * 60 * 1000, // 24 hours
} as const;